import {inject, type InjectionKey, onMounted, onUnmounted, provide, type Ref, ref, watchEffect} from "vue";

export interface LoadingIndicatorService {
  loadingIndicator: Ref<boolean>

  showLoadingIndicator<T>(operation: Promise<T>): Promise<T>
}

const loadingServiceToken = Symbol("loading-service") as InjectionKey<LoadingIndicatorService>

export function provideLoadingIndicator(loaderElementId = "loading"): LoadingIndicatorService {
  const shouldBeLoading = ref<boolean>(false)
  const currentlyLoading = ref<boolean>(false)

  const loaderElement = document.getElementById(loaderElementId)!
  const updateCurrentlyLoading = () => currentlyLoading.value = loaderElement.style.display !== 'none'
  const loaderMutationObserver = new MutationObserver(updateCurrentlyLoading)

  onMounted(() => {
    loaderElement.style.background = 'transparent'
    loaderMutationObserver.observe(loaderElement, {attributes: true, attributeFilter: ['style']});
    updateCurrentlyLoading()
  })
  onUnmounted(() => {
    loaderMutationObserver.disconnect();
  })

  watchEffect(() => {
    if (shouldBeLoading.value != currentlyLoading.value) {
      loaderElement.style.display = shouldBeLoading.value ? 'unset' : 'none'
    }
  })

  const loadingService = createService(shouldBeLoading)
  provide(loadingServiceToken, loadingService)
  return loadingService
}

export function useLoadingIndicator(): LoadingIndicatorService {
  return inject(loadingServiceToken, () => {
    console.warn("Missing LoadingIndicatorService, using no-op")
    const loading = ref<boolean>(false)
    return createService(loading)
  }, true)
}

function createService(loading: Ref<boolean>): LoadingIndicatorService {
  return {
    loadingIndicator: loading,
    showLoadingIndicator<T>(operation: Promise<T>): Promise<T> {
      loading.value = true
      return operation.finally(() => {
        loading.value = false;
      })
    }
  }
}
